// 올리기 전 재인코딩 — 긴 변 2048px JPEG 로 줄이고 EXIF(GPS)를 떨군다. HEIC 도 브라우저가 풀면 JPEG 로 나간다.

export const MAX_EDGE = 2048;
export const JPEG_QUALITY = 0.85;

// 긴 변이 max 를 넘을 때만 줄인다(비율 유지·정수 픽셀).
export const fitWithin = (width: number, height: number, max = MAX_EDGE): { width: number; height: number } => {
  const long = Math.max(width, height);
  if (long <= max) return { width, height };
  const scale = max / long;
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
};

// 캔버스에 그릴 수 있게 푼 사진 — close 로 비트맵·objectURL 을 돌려준다.
export interface DecodedImage {
  source: CanvasImageSource;
  width: number;
  height: number;
  close: () => void;
}

export interface DecodeDeps {
  bitmap?: (file: Blob, options?: ImageBitmapOptions) => Promise<ImageBitmap>;
  img: (file: Blob) => Promise<DecodedImage>;
}

const fromBitmap = (bitmap: ImageBitmap): DecodedImage => ({
  source: bitmap,
  width: bitmap.width,
  height: bitmap.height,
  close: () => bitmap.close(),
});

// createImageBitmap(옵션) → 옵션 없이 → <img> 순으로 푼다. 옵션을 모르는 옛 브라우저(iOS 15 이하)는 TypeError 를 던진다.
export const decodeImage = async (file: Blob, deps: DecodeDeps): Promise<DecodedImage> => {
  if (deps.bitmap) {
    try {
      return fromBitmap(await deps.bitmap(file, { imageOrientation: "from-image" }));
    } catch {
      // 다음 방법으로 넘어간다.
    }
    try {
      return fromBitmap(await deps.bitmap(file));
    } catch {
      // 다음 방법으로 넘어간다.
    }
  }
  return deps.img(file);
};

// <img> 로 푼다 — 브라우저가 EXIF 회전을 기본으로 적용한다.
const decodeViaImg = (file: Blob): Promise<DecodedImage> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () =>
      resolve({
        source: img,
        width: img.naturalWidth,
        height: img.naturalHeight,
        close: () => URL.revokeObjectURL(url),
      });
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("사진을 열 수 없습니다"));
    };
    img.src = url;
  });

const browserDeps = (): DecodeDeps => ({
  bitmap: typeof createImageBitmap === "function" ? (file, options) => createImageBitmap(file, options) : undefined,
  img: decodeViaImg,
});

export const resizeImage = async (file: Blob): Promise<Blob> => {
  const image = await decodeImage(file, browserDeps());
  try {
    const size = fitWithin(image.width, image.height);
    const canvas = document.createElement("canvas");
    canvas.width = size.width;
    canvas.height = size.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("캔버스를 열 수 없습니다");
    ctx.drawImage(image.source, 0, 0, size.width, size.height);
    return await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error("사진을 줄이지 못했습니다"))),
        "image/jpeg",
        JPEG_QUALITY
      )
    );
  } finally {
    image.close();
  }
};
